import { Button } from "components/button/Button";
import config from "config/config";
import {
  buildThreadLink,
  extractThreadUUID
} from "helpers/inbox/messageQueryParam";
import { useRequiredCustomerId } from "hooks/use-customer-id";
import { InboxType, Thread } from "models/inbox/inbox";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router";
import { useRootStore } from "store";
import ThreadSidebarItem from "views/inbox/components/Inbox/sidebar/treadSidebarItem";
import NoMessagesToShowInfoText from "../noMessagesToShowInfoText/noMessagesToShowInfoText";

type Props = {
  type: InboxType;
  threads: Thread[];
  shouldScroll: boolean;
  setShouldScroll: (value: boolean) => void;
};

export default function ThreadSidebarList({
  type,
  threads,
  shouldScroll,
  setShouldScroll
}: Props) {
  const customerId = useRequiredCustomerId();
  const {
    sentMessagesSearchTerm,
    myTotalThreads,
    totalThreads,
    fetchMoreThreads
  } = useRootStore((state) => state.inbox);

  const userInfoId = useRootStore((state) => state.user.userInfo?.id ?? "");

  const threadsWithMeta = useMemo(() => {
    return threads.map((thread) => ({
      ...thread,
      subscriber: thread.subscribers.find((sub) => sub.id === userInfoId),
      threadLink: buildThreadLink(type, thread.threadId, customerId)
    }));
  }, [threads, userInfoId, type, customerId]);

  const bottomRef = useRef<HTMLDivElement | null>(null);
  const activeThreadRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const { threadId: currentThreadId } = useParams();
  // Scroll to the active thread when it changes
  useEffect(() => {
    if (currentThreadId && activeThreadRefs.current) {
      // Find matching thread by comparing UUIDs
      const matchingThread = threadsWithMeta.find(
        (t) => extractThreadUUID(t.threadId) === currentThreadId
      );

      if (matchingThread && activeThreadRefs.current[matchingThread.threadId]) {
        activeThreadRefs.current[matchingThread.threadId]?.scrollIntoView({
          behavior: "smooth",
          block: "center"
        });
      }
    }
  }, [currentThreadId, threadsWithMeta]);

  const threadsLeftToLoad =
    type === "my"
      ? myTotalThreads - threadsWithMeta.length
      : totalThreads - threadsWithMeta.length;

  const nextThreadsToLoadCount = Math.min(
    threadsLeftToLoad,
    config.defaults.pageSize
  );

  useEffect(() => {
    if (!shouldScroll || !bottomRef.current) {
      return;
    }

    bottomRef.current.scrollIntoView({ behavior: "smooth" });

    setShouldScroll(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldScroll, threads.length]);

  const handleLoadMore = useCallback(() => {
    fetchMoreThreads(
      customerId,
      type !== "my",
      threads.length,
      sentMessagesSearchTerm
    );
    setShouldScroll(true);
  }, [
    type,
    threads.length,
    fetchMoreThreads,
    customerId,
    setShouldScroll,
    sentMessagesSearchTerm
  ]);

  if (!threads.length) {
    return <NoMessagesToShowInfoText />;
  }

  return (
    <>
      {threadsWithMeta.map((thread) => (
        <div
          key={thread.threadId}
          ref={(el) => {
            if (el) {
              activeThreadRefs.current[thread.threadId] = el;
            }
          }}
        >
          <ThreadSidebarItem
            thread={thread}
            isSubscribing={thread.subscriber !== undefined}
            threadLink={thread.threadLink}
          />
        </div>
      ))}

      {/*  Dummy element used for scrolling to more threads */}
      <div ref={bottomRef} style={{ height: 1 }} />

      {threadsLeftToLoad > 0 && (
        <div className="d-flex justify-content-center mt-3">
          <Button variant="outline" size="sm" onClick={handleLoadMore}>
            Visa nästa {nextThreadsToLoadCount} konversationer
          </Button>
        </div>
      )}
    </>
  );
}
